/**
 * InventoryContext: ownership del usuario autenticado. Updates optimistas:
 * la UI cambia al instante, el PUT al backend va en background. Si falla, revierte.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchMyInventory, setOwnership as apiSetOwnership } from "../api/inventory";
import { useAuth } from "./AuthContext";

// Tope del cycle de tap. Tras llegar a CYCLE_MAX, el siguiente tap vuelve a 0.
// Para counts mayores el user usa long-press / right-click (promptCount).
const CYCLE_MAX = 5;

interface InventoryValue {
  ownership: Record<number, number>;        // sticker_id → count
  loading: boolean;
  error: string | null;
  setCount: (stickerId: number, count: number) => Promise<void>;
  cycleCount: (stickerId: number) => Promise<void>;   // 0 → 1 → 2 → ... → 5 → 0
  promptCount: (stickerId: number) => Promise<void>;  // abre prompt para tipear N exacto (0-99)
  countOf: (stickerId: number) => number;
  /** Refresca el inventario desde el backend. Llamar tras eventos externos
   *  como completar un trade con apply_inventory=true. */
  refresh: () => Promise<void>;
}

const InventoryContext = createContext<InventoryValue | null>(null);

export function InventoryProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [ownership, setOwnership] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== "authenticated") {
      setOwnership({});
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchMyInventory()
      .then((rows) => {
        const map: Record<number, number> = {};
        for (const r of rows) map[r.sticker_id] = r.count;
        setOwnership(map);
        setError(null);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [status]);

  const setCount = useCallback(async (stickerId: number, count: number) => {
    const prev = ownership[stickerId] ?? 0;
    // Optimistic update
    setOwnership((o) => ({ ...o, [stickerId]: count }));
    try {
      await apiSetOwnership(stickerId, count);
    } catch (e) {
      // Revert on failure
      setOwnership((o) => ({ ...o, [stickerId]: prev }));
      throw e;
    }
  }, [ownership]);

  const cycleCount = useCallback(async (stickerId: number) => {
    const cur = ownership[stickerId] ?? 0;
    // 0 → 1 → 2 → 3 → 4 → 5 → 0. Para más, usar promptCount.
    const next = cur >= CYCLE_MAX ? 0 : cur + 1;
    await setCount(stickerId, next);
  }, [ownership, setCount]);

  const promptCount = useCallback(async (stickerId: number) => {
    const cur = ownership[stickerId] ?? 0;
    const raw = window.prompt(
      `¿Cuántas tienes de esta lámina? (0-99)\n\nActual: ${cur}`,
      String(cur),
    );
    if (raw === null) return;       // cancelado
    const n = parseInt(raw.trim(), 10);
    if (!Number.isFinite(n) || n < 0 || n > 99) {
      alert("Valor inválido. Debe ser un número entre 0 y 99.");
      return;
    }
    if (n === cur) return;
    await setCount(stickerId, n);
  }, [ownership, setCount]);

  const refresh = useCallback(async () => {
    const rows = await fetchMyInventory();
    const map: Record<number, number> = {};
    for (const r of rows) map[r.sticker_id] = r.count;
    setOwnership(map);
  }, []);

  const value = useMemo<InventoryValue>(() => ({
    ownership,
    loading,
    error,
    setCount,
    cycleCount,
    promptCount,
    refresh,
    countOf: (id) => ownership[id] ?? 0,
  }), [ownership, loading, error, setCount, cycleCount, promptCount, refresh]);

  return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>;
}

export function useInventory(): InventoryValue {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error("useInventory() debe estar dentro de <InventoryProvider>");
  return ctx;
}
